import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaExclamationCircle, FaPhone, FaCheckCircle, FaStethoscope, FaHeartbeat, FaBrain, FaSyringe, FaAllergies, FaPills, FaCut } from 'react-icons/fa';

const Emergency = ({ handleEmergencySubmit, setStep }) => {
  const [selectedEmergencies, setSelectedEmergencies] = useState([]);
  const [showTreatmentOptions, setShowTreatmentOptions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const emergencyTypes = {
    "Respiratory": [
      "Laryngospasm",
      "Bronchospasm",
      "Aspiration of Foreign Body",
      "Respiratory Failure",
      "Hypoxia"
    ],
    "Cardiovascular": [
      "Myocardial Infarction",
      "Hypotension",
      "Hypertension",
      "Bradycardia",
      "Tachycardia",
      "Ventricular Arrhythmia",
      "Atrial Arrhythmia"
    ],
    "Neurological": [
      "Stroke",
      "Seizure",
      "Syncope",
      "Delayed Emergence"
    ],
    "Immune Reaction": [
      "Anaphylaxis",
      "Drug Induced Urticaria"
    ],
    "Anesthesia Related Event": [
      "Malignant Hyperthermia",
      "Intra-arterial Injection",
      "Air Embolism"
    ],
    "Medication Related": [
      "Local Anesthetic Overdose",
      "Benzodiazepine Overdose",
      "Opiate Overdose",
      "Medication Error"
    ],
    "Surgical": [
      "Hemorrhage"
    ]
  };

  const categoryIcons = {
    "Respiratory": FaStethoscope,
    "Cardiovascular": FaHeartbeat,
    "Neurological": FaBrain,
    "Immune Reaction": FaAllergies,
    "Anesthesia Related Event": FaSyringe,
    "Medication Related": FaPills,
    "Surgical": FaCut
  };

  // Color scheme for each category
  const categoryColors = {
    "Respiratory": {
      iconBg: "bg-sky-100",
      iconColor: "text-sky-600",
      buttonBg: "bg-sky-50",
      buttonBgSelected: "bg-sky-100",
      buttonBorder: "border-sky-200",
      buttonBorderSelected: "border-sky-400",
      checkboxChecked: "checked:border-sky-600 checked:bg-sky-600",
      checkboxFocus: "focus-visible:outline-sky-600"
    },
    "Cardiovascular": {
      iconBg: "bg-rose-100",
      iconColor: "text-rose-600",
      buttonBg: "bg-rose-50",
      buttonBgSelected: "bg-rose-100",
      buttonBorder: "border-rose-200",
      buttonBorderSelected: "border-rose-400",
      checkboxChecked: "checked:border-rose-600 checked:bg-rose-600",
      checkboxFocus: "focus-visible:outline-rose-600"
    },
    "Neurological": {
      iconBg: "bg-violet-100",
      iconColor: "text-violet-600",
      buttonBg: "bg-violet-50",
      buttonBgSelected: "bg-violet-100",
      buttonBorder: "border-violet-200",
      buttonBorderSelected: "border-violet-400",
      checkboxChecked: "checked:border-violet-600 checked:bg-violet-600",
      checkboxFocus: "focus-visible:outline-violet-600"
    },
    "Immune Reaction": {
      iconBg: "bg-amber-100",
      iconColor: "text-amber-600",
      buttonBg: "bg-amber-50",
      buttonBgSelected: "bg-amber-100",
      buttonBorder: "border-amber-200",
      buttonBorderSelected: "border-amber-400",
      checkboxChecked: "checked:border-amber-600 checked:bg-amber-600",
      checkboxFocus: "focus-visible:outline-amber-600"
    },
    "Anesthesia Related Event": {
      iconBg: "bg-teal-100",
      iconColor: "text-teal-600",
      buttonBg: "bg-teal-50",
      buttonBgSelected: "bg-teal-100",
      buttonBorder: "border-teal-200",
      buttonBorderSelected: "border-teal-400",
      checkboxChecked: "checked:border-teal-600 checked:bg-teal-600",
      checkboxFocus: "focus-visible:outline-teal-600"
    },
    "Medication Related": {
      iconBg: "bg-indigo-100",
      iconColor: "text-indigo-600",
      buttonBg: "bg-indigo-50",
      buttonBgSelected: "bg-indigo-100",
      buttonBorder: "border-indigo-200",
      buttonBorderSelected: "border-indigo-400",
      checkboxChecked: "checked:border-indigo-600 checked:bg-indigo-600",
      checkboxFocus: "focus-visible:outline-indigo-600"
    },
    "Surgical": {
      iconBg: "bg-slate-100",
      iconColor: "text-slate-600",
      buttonBg: "bg-slate-50",
      buttonBgSelected: "bg-slate-200",
      buttonBorder: "border-slate-200",
      buttonBorderSelected: "border-slate-400",
      checkboxChecked: "checked:border-slate-600 checked:bg-slate-600",
      checkboxFocus: "focus-visible:outline-slate-600"
    }
  };

  const handleCheckboxChange = (event) => {
    const { value, checked } = event.target;
    setSelectedEmergencies(prevState =>
      checked
        ? [...prevState, value]
        : prevState.filter(emergency => emergency !== value)
    );
  };

  const handleSubmit = async () => {
    if (selectedEmergencies.length === 0) {
      toast.error('Please select at least one emergency type');
      return;
    }

    setIsSubmitting(true);
    
    // Prepare treatment options data
    const treatmentsData = {};
    selectedEmergencies.forEach(emergency => {
      if (treatmentOptions[emergency]) {
        treatmentsData[emergency] = treatmentOptions[emergency];
      }
    });

    // Submit emergency data with treatment options
    await handleEmergencySubmit({
      emergencies: selectedEmergencies,
      treatmentOptions: treatmentsData,
      submittedAt: new Date().toISOString()
    });

    setIsSubmitting(false);
    // Show treatment options after successful submission
    setShowTreatmentOptions(true);
  };

  const handleDone = () => {
    setStep('mainScreen');
  };

  const treatmentOptions = {
    // RESPIRATORY
    "Laryngospasm": {
      title: "Laryngospasm Management Protocol",
      sections: [
        {
          heading: "Immediate Actions",
          items: [
            "Administer 100% oxygen via nasal mask",
            "Suction the oropharynx, hypopharynx, and nasopharynx with a tonsil suction tip",
            "Remove all blood, saliva, and foreign material from the oral cavity",
            "Pack the surgical site to prevent bleeding into the hypopharynx",
            "Draw the tongue and/or mandible forward"
          ]
        },
        {
          heading: "Assessment",
          items: [
            "Depress the patient's chest while listening with ear close to patient's mouth for a rush of air",
            "If a clear 'huff' of air is heard, the airway is patent and the spasm is likely resolved"
          ]
        },
        {
          heading: "If No Clear 'Huff' is Heard",
          items: [
            "Attempt to break spasm mechanically by ventilating with full face mask and 100% oxygen",
            "Deepen anesthetic with 0.5 mg/kg propofol before administering muscle relaxant",
            "Administer IV succinylcholine: Partial spasm 10-20 mg; Complete spasm 20-40 mg",
            "Alternative: Rocuronium 10-15 mg IV",
            "For complete airway obstruction: Succinylcholine intubating dose, secure airway",
            "Pediatric patients: Consider atropine with repeat succinylcholine to prevent bradycardia"
          ]
        }
      ]
    },

    "Bronchospasm": {
      title: "Bronchospasm Management Protocol",
      sections: [
        {
          heading: "Awake Patient",
          items: [
            "Administer 4-8 puffs of beta-agonist inhalant via inhaler or nebulizer",
            "Pediatric dose: 2-4 puffs",
            "Administer supplemental oxygen via face mask"
          ]
        },
        {
          heading: "Obtunded Non-Intubated Patient",
          items: [
            "Consider albuterol nebulizer via facemask prior to epinephrine administration",
            "Administer 0.3-0.5 mg epinephrine (1:1,000 solution) subcutaneously",
            "If hypotension present (possible anaphylaxis): IV bolus 10-20 mcg epinephrine (1:10,000)",
            "Alternative: 1 mg epinephrine in 500 mL D5W (2 mcg/mL) at 2-10 mcg/min"
          ]
        },
        {
          heading: "Intubated Patient",
          items: [
            "Administer 5-10 puffs of beta-agonist into endotracheal tube",
            "Consider deepening plane of anesthetic",
            "Check breathing circuit",
            "If persistent, administer epinephrine as above",
            "Administer 100% oxygen with positive pressure ventilation"
          ]
        }
      ]
    },

    "Aspiration of Foreign Body": {
      title: "Aspiration of Foreign Body Management Protocol",
      sections: [
        {
          heading: "Immediate Positioning",
          items: [
            "Place patient in Trendelenburg position with head down at least 15 degrees",
            "Roll patient onto right side"
          ]
        },
        {
          heading: "Airway Clearance",
          items: [
            "Clear vomitus from oropharynx with finger sweeps and large-bore suction",
            "If no improvement, place patient supine and intubate (muscle relaxants may be required)",
            "Remove solid particles in laryngeal region with Magill forceps during intubation"
          ]
        },
        {
          heading: "Supportive Care",
          items: [
            "Oxygenate patient as soon as possible",
            "Manage any associated bronchospasm per protocol",
            "Tracheobronchial lavage: Only small volumes (5-10 mL saline) to facilitate suction",
            "Maintain patent airway and continue ventilation with 100% oxygen during transport"
          ]
        }
      ]
    },

    "Respiratory Failure": {
      title: "Respiratory Failure Management Protocol",
      sections: [
        {
          heading: "Immediate Assessment",
          items: [
            "Assess airway patency and breathing effort",
            "Check oxygen saturation and apply continuous pulse oximetry",
            "Evaluate respiratory rate, depth, and pattern"
          ]
        },
        {
          heading: "Airway Management",
          items: [
            "Administer 100% oxygen via high-flow face mask",
            "Position patient to optimize airway (head tilt, chin lift, jaw thrust)",
            "Consider oral/nasal airway adjuncts",
            "Prepare for intubation if patient deteriorates"
          ]
        },
        {
          heading: "Ventilatory Support",
          items: [
            "Initiate bag-valve-mask ventilation if spontaneous breathing inadequate",
            "Consider non-invasive positive pressure ventilation if appropriate",
            "Proceed to endotracheal intubation if conservative measures fail",
            "Activate EMS for hospital transfer with advanced airway management"
          ]
        }
      ]
    },

    "Hypoxia": {
      title: "Hypoxia Management Protocol",
      sections: [
        {
          heading: "Immediate Actions",
          items: [
            "Administer 100% oxygen by face mask",
            "Ensure patent airway - chin lift, jaw thrust, suction if needed",
            "Position patient appropriately (semi-Fowler's if conscious)"
          ]
        },
        {
          heading: "Assessment & Monitoring",
          items: [
            "Apply pulse oximetry for continuous SpO2 monitoring",
            "Assess respiratory effort and breath sounds",
            "Check for airway obstruction or secretions"
          ]
        },
        {
          heading: "Interventions Based on Cause",
          items: [
            "Airway obstruction: Clear airway, consider advanced airway",
            "Hypoventilation: Assist ventilation, consider reversal agents",
            "Bronchospasm: Treat per bronchospasm protocol",
            "Consider reversal of sedative medications if applicable",
            "Prepare for intubation if oxygen saturation remains low"
          ]
        }
      ]
    },

    // CARDIOVASCULAR
    "Myocardial Infarction": {
      title: "Acute Myocardial Infarction (STEMI/NSTEMI) Protocol",
      sections: [
        {
          heading: "Immediate Actions",
          items: [
            "Terminate all surgical procedures and patient stimulation",
            "Position patient comfortably and loosen tight clothing",
            "Activate EMS (911) immediately",
            "Administer oxygen at 4 L/min via mask or nasal cannula"
          ]
        },
        {
          heading: "Monitoring",
          items: [
            "Apply pulse oximeter and ECG monitor",
            "Obtain vital signs with automatic or standard BP cuff",
            "Establish IV access if not already obtained"
          ]
        },
        {
          heading: "MONA Protocol (O-N-A-M Order)",
          items: [
            "Oxygen: 4 L/min via mask or nasal cannula",
            "Nitroglycerin: Sublingual or spray every 5 min if SBP > 90 mmHg",
            "Aspirin: Non-enteric 160-325 mg (chew one, swallow one) unless allergic",
            "Morphine: 1-3 mg IV every 5 min for pain control if SBP > 90 mmHg"
          ]
        },
        {
          heading: "Monitoring for Complications",
          items: [
            "Watch for hypotension, hypoxemia, bradycardia",
            "Monitor for ventricular dysrhythmias",
            "Be prepared to initiate ACLS protocols if cardiac arrest occurs"
          ]
        }
      ]
    },

    "Hypotension": {
      title: "Hypotension Management Protocol",
      sections: [
        {
          heading: "Immediate Actions",
          items: [
            "Decrease anesthetic depth if patient under anesthesia",
            "Terminate surgical procedure",
            "Place patient supine with legs elevated",
            "Administer 100% oxygen"
          ]
        },
        {
          heading: "Assessment",
          items: [
            "Re-evaluate blood pressure, heart rate, and rhythm",
            "Identify and treat underlying cause",
            "Treat dysrhythmias if present"
          ]
        },
        {
          heading: "Pharmacological Treatment",
          items: [
            "Administer bolus of isotonic crystalloid fluid",
            "Ephedrine: 2.5-5 mg IV (dilute 50 mg in 9 mL saline = 5 mg/mL), repeat until BP stabilized",
            "Phenylephrine: 0.1 mg IV increments (for tachycardia with hypotension)",
            "Epinephrine infusion: 1 mg in 500 mL D5W (2 mcg/mL) at 2-10 mcg/min if severe"
          ]
        }
      ]
    },

    "Hypertension": {
      title: "Hypertensive Emergency Management Protocol",
      sections: [
        {
          heading: "Treatment Goals",
          items: [
            "Produce vasodilation or reduce cardiac output",
            "Reduce blood pressure gradually to avoid hypoperfusion",
            "Identify and address underlying causes"
          ]
        },
        {
          heading: "Beta-Blocker Options",
          items: [
            "Esmolol: 80 mg (1 mg/kg) over 30 sec, then 150 mcg/kg/min infusion (max 300 mcg/kg/min)",
            "Metoprolol: 5 mg IV over 2 minutes"
          ]
        },
        {
          heading: "Other Antihypertensives",
          items: [
            "Labetalol: 5-20 mg IV over 2 min, then 2 mg/min infusion (max 300 mg)",
            "Hydralazine: 5-25 mg IV (for patients with asthma who can tolerate increased HR)"
          ]
        }
      ]
    },

    "Bradycardia": {
      title: "Bradycardia Management Protocol",
      sections: [
        {
          heading: "Initial Assessment",
          items: [
            "Confirm heart rate < 60 bpm with signs/symptoms of poor perfusion",
            "Assess for underlying cause (medications, vagal stimulation, hypoxia)",
            "Monitor blood pressure and oxygen saturation"
          ]
        },
        {
          heading: "Treatment",
          items: [
            "Administer 100% oxygen",
            "Atropine: 0.5 mg IV every 3-5 minutes (max 3 mg total)",
            "If atropine ineffective: Consider transcutaneous pacing",
            "Epinephrine infusion: 2-10 mcg/min if pacing unavailable"
          ]
        },
        {
          heading: "Refractory Bradycardia",
          items: [
            "Dopamine infusion: 5-20 mcg/kg/min",
            "Consider transvenous pacing",
            "Activate EMS for hospital transfer"
          ]
        }
      ]
    },

    "Tachycardia": {
      title: "Tachycardia Management Protocol",
      sections: [
        {
          heading: "Initial Assessment",
          items: [
            "Confirm heart rate > 100 bpm",
            "Determine if patient is stable or unstable (hypotension, altered mental status, chest pain)",
            "Obtain 12-lead ECG if available to determine rhythm type"
          ]
        },
        {
          heading: "Unstable Patient",
          items: [
            "Prepare for immediate synchronized cardioversion",
            "Activate EMS immediately",
            "Administer sedation if patient conscious"
          ]
        },
        {
          heading: "Stable Narrow Complex Tachycardia",
          items: [
            "Attempt vagal maneuvers (Valsalva, carotid massage)",
            "Adenosine: 6 mg rapid IV push, followed by 12 mg if no response",
            "Consider beta-blockers or calcium channel blockers"
          ]
        },
        {
          heading: "Stable Wide Complex Tachycardia",
          items: [
            "Assume ventricular tachycardia until proven otherwise",
            "Amiodarone: 150 mg IV over 10 minutes",
            "Prepare for cardioversion if patient becomes unstable"
          ]
        }
      ]
    },

    "Ventricular Arrhythmia": {
      title: "Ventricular Arrhythmia Management Protocol",
      sections: [
        {
          heading: "Ventricular Tachycardia - Pulseless",
          items: [
            "Begin CPR immediately",
            "Defibrillate: 200J biphasic (360J monophasic)",
            "Resume CPR for 2 minutes",
            "Epinephrine: 1 mg IV/IO every 3-5 minutes",
            "Amiodarone: 300 mg IV/IO bolus, then 150 mg"
          ]
        },
        {
          heading: "Ventricular Tachycardia - With Pulse",
          items: [
            "If unstable: Synchronized cardioversion 100J",
            "If stable: Amiodarone 150 mg IV over 10 minutes",
            "Alternative: Lidocaine 1-1.5 mg/kg IV"
          ]
        },
        {
          heading: "Ventricular Fibrillation",
          items: [
            "Begin CPR and defibrillate immediately",
            "Follow ACLS VF/pVT algorithm",
            "Consider reversible causes (H's and T's)"
          ]
        }
      ]
    },

    "Atrial Arrhythmia": {
      title: "Atrial Arrhythmia Management Protocol",
      sections: [
        {
          heading: "Atrial Fibrillation/Flutter - Unstable",
          items: [
            "Synchronized cardioversion: Start at 120-200J biphasic",
            "Sedate patient if conscious",
            "Activate EMS for transfer"
          ]
        },
        {
          heading: "Atrial Fibrillation/Flutter - Stable with Rapid Rate",
          items: [
            "Rate control is primary goal",
            "Diltiazem: 0.25 mg/kg IV over 2 min (typically 15-20 mg)",
            "Alternative: Metoprolol 5 mg IV over 2 minutes",
            "Alternative: Amiodarone 150 mg IV over 10 minutes"
          ]
        },
        {
          heading: "Supraventricular Tachycardia",
          items: [
            "Vagal maneuvers first (Valsalva, carotid massage)",
            "Adenosine: 6 mg rapid IV push with flush",
            "If no response: Adenosine 12 mg rapid IV push",
            "Consider cardioversion if refractory"
          ]
        }
      ]
    },

    // NEUROLOGICAL
    "Stroke": {
      title: "Acute Stroke Management Protocol",
      sections: [
        {
          heading: "Recognition (FAST)",
          items: [
            "Face drooping",
            "Arm weakness",
            "Speech difficulty",
            "Time to call 911"
          ]
        },
        {
          heading: "Immediate Actions",
          items: [
            "Activate EMS (911) immediately - time critical emergency",
            "Note time of symptom onset (crucial for treatment decisions)",
            "Position patient with head elevated 30 degrees",
            "Do NOT administer aspirin or other antithrombotics"
          ]
        },
        {
          heading: "Supportive Care",
          items: [
            "Maintain airway and administer oxygen if SpO2 < 94%",
            "Monitor vital signs continuously",
            "Do NOT lower blood pressure unless extremely elevated (>220/120)",
            "Keep patient NPO",
            "Provide reassurance and keep patient calm"
          ]
        }
      ]
    },

    "Seizure": {
      title: "Seizure Management Protocol",
      sections: [
        {
          heading: "During Active Seizure",
          items: [
            "Protect patient from injury - clear area of hazards",
            "Do NOT restrain patient or place anything in mouth",
            "Position on side (recovery position) when possible",
            "Loosen restrictive clothing around neck",
            "Note time seizure began"
          ]
        },
        {
          heading: "Post-Ictal Care",
          items: [
            "Maintain airway and administer oxygen",
            "Suction secretions as needed",
            "Place patient in recovery position",
            "Check blood glucose"
          ]
        },
        {
          heading: "Prolonged Seizure (>5 minutes) or Status Epilepticus",
          items: [
            "Activate EMS immediately",
            "Midazolam: 2.5-5 mg IV/IM every 5 minutes to effect",
            "Alternative: Diazepam 5-10 mg IV over 1 minute",
            "Prepare for airway management",
            "If local anesthetic toxicity suspected: Prepare for lipid emulsion therapy"
          ]
        }
      ]
    },

    "Syncope": {
      title: "Syncope Management Protocol",
      sections: [
        {
          heading: "Immediate Actions",
          items: [
            "Place patient in reclining position with legs elevated",
            "Ensure patent airway",
            "Loosen restrictive clothing"
          ]
        },
        {
          heading: "Assessment & Monitoring",
          items: [
            "Monitor vital signs",
            "Deliver 100% oxygen",
            "Apply cool compress to forehead",
            "Administer spirits of ammonia if necessary"
          ]
        },
        {
          heading: "If Bradycardia Present",
          items: [
            "Atropine: 0.5 mg IV every 3-5 minutes up to 3 mg total",
            "Support respiration if needed",
            "Continue monitoring until full recovery"
          ]
        }
      ]
    },

    "Delayed Emergence": {
      title: "Delayed Emergence Management Protocol",
      sections: [
        {
          heading: "Assessment",
          items: [
            "Review total anesthetic doses administered",
            "Calculate expected recovery time based on agents used",
            "Check blood glucose level",
            "Assess temperature and consider hypothermia"
          ]
        },
        {
          heading: "Supportive Care",
          items: [
            "Maintain airway and adequate oxygenation",
            "Continue monitoring vital signs",
            "Ensure patient is normothermic",
            "Allow adequate time for drug metabolism"
          ]
        },
        {
          heading: "Reversal Considerations",
          items: [
            "Flumazenil for benzodiazepine reversal: 0.2 mg IV, may repeat to max 1 mg",
            "Naloxone for opioid reversal: 0.04-0.4 mg IV, titrate to effect",
            "If no improvement, consider other causes (stroke, hypoglycemia, hypothermia)",
            "Activate EMS if prolonged unresponsiveness"
          ]
        }
      ]
    },

    // IMMUNE REACTION
    "Anaphylaxis": {
      title: "Anaphylaxis Management Protocol",
      sections: [
        {
          heading: "Immediate Actions",
          items: [
            "Stop administration of suspected antigen",
            "Call for help and activate EMS"
          ]
        },
        {
          heading: "ABCs",
          items: [
            "Airway: Maintain with supplemental 100% oxygen",
            "Breathing: Support respiration, prepare for intubation",
            "Circulation: Establish IV access, rapid crystalloid infusion (1 L adults, 20 mL/kg pediatric)"
          ]
        },
        {
          heading: "Epinephrine (Primary Treatment)",
          items: [
            "IV Route: 0.2-0.5 mg (2-5 mL of 1:10,000) titrated to effect, repeat q2-5 min",
            "IM Route (if no IV): 0.3-0.5 mg of 1:1,000, repeat q10-20 min",
            "Pediatric: 0.01 mg/kg"
          ]
        },
        {
          heading: "Adjunctive Medications",
          items: [
            "Diphenhydramine (H1 blocker): 50 mg IV adults, 25 mg ages 6-12",
            "Famotidine (H2 blocker): 20 mg IV adults, 0.5 mg/kg pediatric",
            "Albuterol inhaler: 4-5 puffs as needed for bronchospasm",
            "Corticosteroids: Hydrocortisone 100 mg IV or Dexamethasone 4-12 mg IV/IM"
          ]
        }
      ]
    },

    "Drug Induced Urticaria": {
      title: "Drug Induced Urticaria Management Protocol",
      sections: [
        {
          heading: "Mild Reaction (Skin Only)",
          items: [
            "Discontinue suspected causative agent",
            "Monitor for progression to systemic symptoms",
            "Document suspected allergen in patient record"
          ]
        },
        {
          heading: "Pharmacological Treatment",
          items: [
            "Diphenhydramine: 25-50 mg PO or 10-50 mg IV (max 25 mg/min)",
            "Alternative: Chlorpheniramine 4 mg PO or 5-20 mg IV/IM",
            "Consider H2 blocker for refractory cases"
          ]
        },
        {
          heading: "Monitoring",
          items: [
            "Observe for 30-60 minutes for progression",
            "Watch for signs of anaphylaxis (respiratory distress, hypotension)",
            "If symptoms progress, treat as anaphylaxis",
            "Discharge instructions: Avoid suspected allergen, carry antihistamines"
          ]
        }
      ]
    },

    // ANESTHESIA RELATED
    "Malignant Hyperthermia": {
      title: "Malignant Hyperthermia Management Protocol",
      sections: [
        {
          heading: "Recognition - Primary Signs",
          items: [
            "Increased end-tidal CO2 (may double or triple)",
            "Unexplained tachycardia, tachypnea, hypercarbia",
            "Generalized muscle rigidity",
            "Masseter muscle rigidity",
            "Hyperthermia (often late sign)",
            "Sudden unexpected cardiac arrest"
          ]
        },
        {
          heading: "Immediate Actions",
          items: [
            "Call MH Hotline: 1-800-644-9737 (1-800-MH-HYPER)",
            "Discontinue ALL volatile anesthetics and succinylcholine immediately",
            "Hyperventilate with 100% oxygen at high flows (≥10 L/min)",
            "Activate EMS for immediate hospital transfer"
          ]
        },
        {
          heading: "Dantrolene Administration",
          items: [
            "Initial bolus: 2-3 mg/kg IV rapidly",
            "Continue until signs controlled, up to 10 mg/kg total",
            "May exceed 10 mg/kg if needed"
          ]
        },
        {
          heading: "Supportive Measures",
          items: [
            "Active cooling: Cold IV saline (not LR) 15 mL/kg q15 min x3",
            "Surface cooling with ice packs and cooling blanket",
            "Treat hyperkalemia: Glucose/insulin, bicarbonate, calcium if life-threatening",
            "Treat arrhythmias with standard agents (avoid calcium channel blockers)",
            "Maintain urine output >2 mL/kg/hr with fluids and furosemide"
          ]
        }
      ]
    },

    "Intra-arterial Injection": {
      title: "Intra-arterial Injection Management Protocol",
      sections: [
        {
          heading: "Recognition",
          items: [
            "Severe burning pain along course of artery",
            "Blanching of skin in distribution",
            "May have pulsation of blood in syringe"
          ]
        },
        {
          heading: "Immediate Actions",
          items: [
            "Leave needle/catheter in place",
            "Do NOT withdraw - leave for treatment",
            "Stop injection of any medication immediately"
          ]
        },
        {
          heading: "Treatment",
          items: [
            "Inject 10 mL of 1% plain lidocaine into the artery",
            "Consider heparin 5000 units intra-arterially",
            "Consider papaverine 30-60 mg intra-arterially for vasospasm"
          ]
        },
        {
          heading: "Follow-up",
          items: [
            "Transfer patient to hospital for further therapy",
            "Vascular surgery consultation",
            "Monitor for compartment syndrome",
            "Document incident thoroughly"
          ]
        }
      ]
    },

    "Air Embolism": {
      title: "Venous Air Embolism Management Protocol",
      sections: [
        {
          heading: "Recognition",
          items: [
            "Sudden cardiovascular collapse",
            "Mill wheel murmur on precordial auscultation",
            "Sudden decrease in end-tidal CO2",
            "Hypotension, tachycardia, arrhythmias"
          ]
        },
        {
          heading: "Immediate Actions",
          items: [
            "Flood surgical field with saline",
            "Occlude source of air entry",
            "Administer 100% oxygen",
            "Place patient in left lateral decubitus position with head down (Durant position)"
          ]
        },
        {
          heading: "Supportive Care",
          items: [
            "Support circulation with IV fluids and vasopressors as needed",
            "CPR if cardiac arrest",
            "Aspirate air from central line if present",
            "Activate EMS for hyperbaric oxygen therapy if available"
          ]
        }
      ]
    },

    // MEDICATION RELATED
    "Local Anesthetic Overdose": {
      title: "Local Anesthetic Systemic Toxicity (LAST) Protocol",
      sections: [
        {
          heading: "Recognition",
          items: [
            "CNS: Tinnitus, metallic taste, numbness, agitation, confusion, seizures",
            "Cardiovascular: Hypotension, bradycardia, arrhythmias, cardiac arrest"
          ]
        },
        {
          heading: "Immediate Actions",
          items: [
            "Stop local anesthetic administration",
            "Call for help and LAST kit (lipid emulsion)",
            "Manage airway and administer 100% oxygen",
            "Suppress seizures with benzodiazepines (avoid propofol if cardiovascular instability)"
          ]
        },
        {
          heading: "Lipid Emulsion Therapy (20% Intralipid)",
          items: [
            "Bolus: 1.5 mL/kg IV over 1 minute (approximately 100 mL for 70 kg patient)",
            "Infusion: 0.25 mL/kg/min",
            "Repeat bolus every 3-5 minutes up to 3 mL/kg total",
            "Continue infusion until hemodynamic stability, double rate if BP remains low",
            "Maximum total dose: 10 mL/kg over first 30 minutes"
          ]
        },
        {
          heading: "Cardiac Arrest Considerations",
          items: [
            "Start CPR, consider prolonged resuscitation",
            "AVOID vasopressin, calcium channel blockers, beta-blockers, lidocaine",
            "Use smaller epinephrine doses (<1 mcg/kg)",
            "Consider cardiopulmonary bypass if available"
          ]
        }
      ]
    },

    "Benzodiazepine Overdose": {
      title: "Benzodiazepine Overdose Management Protocol",
      sections: [
        {
          heading: "Recognition",
          items: [
            "Excessive sedation, somnolence",
            "Respiratory depression",
            "Hypotension",
            "Paradoxical agitation (rare)"
          ]
        },
        {
          heading: "Supportive Care",
          items: [
            "Ensure patent airway",
            "Administer 100% oxygen",
            "Support ventilation as needed",
            "Establish IV access if not present"
          ]
        },
        {
          heading: "Flumazenil (Romazicon) Administration",
          items: [
            "Initial dose: 0.2 mg IV over 15-30 seconds",
            "Wait 45 seconds for response",
            "If inadequate: Additional 0.2 mg IV, may repeat at 1-minute intervals",
            "Maximum total dose: 1 mg (most patients respond to 0.3-0.6 mg)"
          ]
        },
        {
          heading: "Cautions",
          items: [
            "Avoid in patients with known seizure disorder",
            "Use caution in chronic benzodiazepine users (may precipitate withdrawal)",
            "May cause resedation - observe for 2 hours minimum",
            "Half-life shorter than most benzodiazepines - monitor for return of sedation"
          ]
        }
      ]
    },

    "Opiate Overdose": {
      title: "Opioid Overdose Management Protocol",
      sections: [
        {
          heading: "Recognition",
          items: [
            "Respiratory depression (RR < 12, shallow breathing)",
            "Pinpoint pupils (miosis)",
            "Altered level of consciousness",
            "Hypotension"
          ]
        },
        {
          heading: "Supportive Care",
          items: [
            "Stimulate patient and maintain airway",
            "Administer 100% oxygen",
            "Provide bag-valve-mask ventilation if needed"
          ]
        },
        {
          heading: "Naloxone (Narcan) Administration",
          items: [
            "Titrated approach: 0.04-0.08 mg IV every 2-3 minutes",
            "Goal: Restore adequate ventilation without full reversal",
            "If no IV access: 0.4 mg IM or 2-4 mg intranasal",
            "May need higher doses for synthetic opioids (fentanyl)"
          ]
        },
        {
          heading: "Monitoring",
          items: [
            "Observe for at least 2 hours (longer for long-acting opioids)",
            "Naloxone duration (30-90 min) shorter than most opioids",
            "Be prepared for repeat dosing or infusion",
            "Watch for opioid withdrawal symptoms in dependent patients"
          ]
        }
      ]
    },

    "Medication Error": {
      title: "Medication Error Response Protocol",
      sections: [
        {
          heading: "Immediate Assessment",
          items: [
            "Identify the medication administered incorrectly",
            "Determine dose, route, and time of administration",
            "Assess patient for any adverse effects",
            "Review expected effects and potential complications"
          ]
        },
        {
          heading: "Patient Care",
          items: [
            "Treat any adverse effects symptomatically",
            "Administer reversal agents if available and indicated",
            "Increase monitoring frequency",
            "Consider hospital transfer if significant adverse effects possible"
          ]
        },
        {
          heading: "Documentation & Reporting",
          items: [
            "Document error completely and factually",
            "Notify appropriate personnel (physician, supervisor)",
            "Complete incident report per facility policy",
            "Communicate with patient/family as appropriate"
          ]
        },
        {
          heading: "Prevention",
          items: [
            "Review medication protocols",
            "Verify all medications prior to administration",
            "Use standardized labeling systems",
            "Conduct root cause analysis"
          ]
        }
      ]
    },

    // DENTAL RELATED
    "Unintentional Damage to a Tooth": {
      title: "Unintentional Dental Damage Protocol",
      sections: [
        {
          heading: "Immediate Assessment",
          items: [
            "Evaluate extent of damage (chip, fracture, avulsion, luxation)",
            "Locate any tooth fragments",
            "Check for aspiration of fragments",
            "Document pre-operative dental condition if available"
          ]
        },
        {
          heading: "Management",
          items: [
            "Control bleeding with pressure",
            "If tooth avulsed, handle by crown only, not root",
            "Store avulsed tooth in saline, milk, or patient's saliva",
            "Splint loose teeth if possible"
          ]
        },
        {
          heading: "Documentation & Follow-up",
          items: [
            "Photograph damage",
            "Document incident completely",
            "Refer to restorative dentist or endodontist as needed",
            "Inform patient and discuss treatment options"
          ]
        }
      ]
    },

    "Wrong Tooth Extraction": {
      title: "Wrong Tooth Extraction Protocol",
      sections: [
        {
          heading: "Immediate Actions",
          items: [
            "Stop procedure immediately upon recognition",
            "Assess if re-implantation is possible",
            "Preserve extracted tooth in appropriate medium"
          ]
        },
        {
          heading: "Patient Communication",
          items: [
            "Inform patient honestly of the error",
            "Discuss immediate treatment options",
            "Document conversation thoroughly"
          ]
        },
        {
          heading: "Treatment Options",
          items: [
            "Consider immediate re-implantation if appropriate",
            "Consult prosthodontist for replacement options",
            "Schedule follow-up to address correct tooth if still indicated"
          ]
        },
        {
          heading: "Reporting",
          items: [
            "Complete incident report",
            "Notify practice leadership",
            "Implement root cause analysis",
            "Review verification protocols"
          ]
        }
      ]
    },

    "Non-Restorable Implant Placement": {
      title: "Non-Restorable Implant Placement Protocol",
      sections: [
        {
          heading: "Recognition",
          items: [
            "Implant positioned outside restorative envelope",
            "Inadequate angulation for prosthetic restoration",
            "Insufficient inter-implant distance",
            "Violation of anatomic structures"
          ]
        },
        {
          heading: "Intraoperative Options",
          items: [
            "If immediately recognized: Consider removal and re-placement",
            "Assess if angulated abutment could compensate",
            "Evaluate if bone grafting could improve position"
          ]
        },
        {
          heading: "Documentation",
          items: [
            "Document position with radiographs",
            "Note any anatomic considerations that contributed",
            "Record discussion with patient"
          ]
        },
        {
          heading: "Management Options",
          items: [
            "Prosthodontic consultation for creative restoration",
            "Consider implant removal and site grafting",
            "Plan for replacement after adequate healing",
            "Discuss all options and prognosis with patient"
          ]
        }
      ]
    },

    // SURGICAL
    "Iatrogenic Nerve Injury": {
      title: "Iatrogenic Nerve Injury Management Protocol",
      sections: [
        {
          heading: "Intraoperative Recognition",
          items: [
            "Direct visualization of nerve damage",
            "Unexpected paresthesia reported by patient under local anesthesia",
            "Bleeding from neurovascular bundle"
          ]
        },
        {
          heading: "Immediate Management",
          items: [
            "Document exact location and nature of injury",
            "If nerve transected: Consider microsurgical repair or referral",
            "Minimize further manipulation of affected nerve"
          ]
        },
        {
          heading: "Postoperative Care",
          items: [
            "Thorough documentation of injury and findings",
            "Inform patient of nerve injury and expected course",
            "Consider corticosteroids in first 24-48 hours",
            "Oral and maxillofacial surgery or neurosurgery consultation"
          ]
        },
        {
          heading: "Follow-up",
          items: [
            "Serial sensory testing and documentation",
            "Patient education on protecting anesthetic areas",
            "Consider referral for microsurgical repair if no recovery",
            "Monitor for neuropathic pain development"
          ]
        }
      ]
    },

    "Hemorrhage": {
      title: "Surgical Hemorrhage Management Protocol",
      sections: [
        {
          heading: "Immediate Hemorrhage Control",
          items: [
            "Apply direct pressure with gauze",
            "Identify source of bleeding",
            "Ensure adequate visualization (suction, retraction, lighting)"
          ]
        },
        {
          heading: "Local Hemostatic Measures",
          items: [
            "Electrocautery for small vessels",
            "Suture ligation for larger vessels",
            "Topical hemostatic agents (Surgicel, Gelfoam, bone wax)",
            "Compression with hemostatic packing"
          ]
        },
        {
          heading: "Systemic Support",
          items: [
            "Establish/confirm IV access",
            "Crystalloid fluid resuscitation",
            "Monitor vital signs closely",
            "Type and screen/crossmatch if significant blood loss"
          ]
        },
        {
          heading: "Uncontrolled Hemorrhage",
          items: [
            "Maintain direct pressure",
            "Activate EMS for hospital transfer",
            "Consider tranexamic acid if available",
            "Prepare for possible transfusion and surgical intervention"
          ]
        }
      ]
    },

    "Soft Tissue Injury": {
      title: "Soft Tissue Injury (Burns, Lacerations) Protocol",
      sections: [
        {
          heading: "Thermal/Electrical Burns",
          items: [
            "Remove heat source immediately",
            "Cool affected area with room temperature saline (not ice)",
            "Assess depth and extent of burn",
            "Document location and size"
          ]
        },
        {
          heading: "Laceration Management",
          items: [
            "Control bleeding with direct pressure",
            "Clean wound thoroughly",
            "Inspect for damage to underlying structures",
            "Repair in layers if needed, minimize tension"
          ]
        },
        {
          heading: "Wound Care",
          items: [
            "Irrigate with sterile saline",
            "Apply appropriate dressing",
            "Consider antibiotic coverage if contaminated",
            "Update tetanus if indicated"
          ]
        },
        {
          heading: "Documentation & Follow-up",
          items: [
            "Photograph injury before and after repair",
            "Complete incident documentation",
            "Provide patient with wound care instructions",
            "Schedule follow-up for wound check and suture removal"
          ]
        }
      ]
    }
  };

  if (showTreatmentOptions) {
    return (
      <div className="min-h-screen bg-[#f3f7fb] py-12 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-8 text-center">
            <div className="inline-flex size-16 items-center justify-center rounded-full bg-[#103a74]/10">
              <FaStethoscope className="size-8 text-[#103a74]" aria-hidden="true" />
            </div>
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Treatment Protocols
            </h1>
            <p className="mt-2 text-base text-gray-600">
              Evidence-based treatment protocols for selected emergencies
            </p>
          </div>

          {/* Treatment Options Card */}
          <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
            <div className="px-6 py-8 sm:px-10 sm:py-10">
              <div className="space-y-10">
                {selectedEmergencies.map((emergency, idx) => {
                  const treatment = treatmentOptions[emergency];
                  if (!treatment) {
                    return (
                  <div key={emergency} className="border-b border-gray-200 pb-8 last:border-b-0 last:pb-0">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">{emergency}</h3>
                        <p className="text-gray-600 italic">Treatment protocol not available</p>
                    </div>
                    );
                  }
                  return (
                    <div key={emergency} className={`${idx > 0 ? 'border-t border-gray-200 pt-10' : ''}`}>
                      {/* Emergency Title */}
                      <div className="mb-6">
                        <h3 className="text-xl font-bold text-[#103a74]">{treatment.title}</h3>
                  </div>
                      
                      {/* Treatment Sections */}
                      <div className="space-y-6">
                        {treatment.sections.map((section, sectionIdx) => (
                          <div key={sectionIdx} className="bg-gray-50 rounded-lg p-5">
                            <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-3 border-b border-gray-200 pb-2">
                              {section.heading}
                            </h4>
                            <ul className="space-y-2">
                              {section.items.map((item, itemIdx) => (
                                <li key={itemIdx} className="flex items-start gap-3 text-sm text-gray-700">
                                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#103a74] mt-2"></span>
                                  <span className="leading-relaxed">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Done Button */}
            <div className="flex items-center justify-center gap-x-4 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:px-10">
              <button
                type="button"
                onClick={handleDone}
                className="inline-flex items-center gap-x-2 rounded-lg bg-[#103a74] px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#0d2d5c] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#103a74] transition-colors"
              >
                <FaCheckCircle aria-hidden="true" className="size-5" />
                <span>Done</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f7fb] py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-red-100">
            <FaExclamationCircle className="size-8 text-red-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Emergency Action Required
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Select the type(s) of emergency that occurred
          </p>
          
          {/* Call 911 Button */}
          <div className="mt-6">
            <a
              href="tel:911"
              className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 transition-colors"
            >
              <FaPhone className="size-5" />
              <span>Call 911</span>
            </a>
          </div>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <div className="space-y-10 px-6 py-8 sm:px-10 sm:py-10">
              {Object.entries(emergencyTypes).map(([category, emergencies]) => {
                const CategoryIcon = categoryIcons[category] || FaStethoscope;
                const colors = categoryColors[category] || categoryColors["Respiratory"];
                return (
                  <div key={category} className="border-b border-gray-200 pb-8 last:border-b-0 last:pb-0">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`flex size-10 items-center justify-center rounded-lg ${colors.iconBg}`}>
                        <CategoryIcon className={`size-5 ${colors.iconColor}`} />
                      </div>
                      <h2 className="text-lg font-bold text-gray-900">
                      {category}
                    </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {emergencies.map((emergency, index) => {
                        const isSelected = selectedEmergencies.includes(emergency);
                        return (
                          <label
                            key={`${category}-${index}`}
                            htmlFor={`emergency-${category}-${index}`}
                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all border ${
                              isSelected
                                ? `${colors.buttonBgSelected} ${colors.buttonBorderSelected}`
                                : `${colors.buttonBg} ${colors.buttonBorder} hover:opacity-80`
                            }`}
                          >
                            <div className="flex shrink-0 items-center">
                              <div className="group grid size-5 grid-cols-1">
                              <input
                                type="checkbox"
                                id={`emergency-${category}-${index}`}
                                value={emergency}
                                  checked={isSelected}
                                onChange={handleCheckboxChange}
                                  className={`col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white ${colors.checkboxChecked} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${colors.checkboxFocus}`}
                              />
                              <svg
                                fill="none"
                                viewBox="0 0 14 14"
                                  className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white"
                              >
                                <path
                                  d="M3 8L6 11L11 3.5"
                                  strokeWidth={2}
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="opacity-0 group-has-[:checked]:opacity-100"
                                />
                              </svg>
                            </div>
                          </div>
                            <span className="text-sm font-medium text-gray-900 flex-1">
                            {emergency}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-center gap-x-4 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:px-10">
              <button
                type="submit"
                disabled={isSubmitting || selectedEmergencies.length === 0}
                className="inline-flex items-center gap-x-2 rounded-lg bg-red-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle aria-hidden="true" className="size-5" />
                    <span>Submit Emergency Report</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Emergency;
